import {
  Alert,
  Badge,
  Box,
  Card,
  Group,
  Loader,
  Progress,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import dayjs from "dayjs";
import "dayjs/locale/fr";
import isBetween from "dayjs/plugin/isBetween";
import { useMemo } from "react";
import {
  Activity,
  AlertCircle,
  TrendingDown,
  TrendingUp,
  Users,
} from "tabler-icons-react";

dayjs.locale("fr");
dayjs.extend(isBetween);

const BORDER_COLORS = {
  blue: "#3b82f6",
  green: "#10b981",
  cyan: "#06b6d4",
  red: "#ef4444",
  orange: "#f97316",
};

const EngagementMetricCard = ({
  title,
  value,
  subtitle,
  icon,
  color,
  badge,
  alert,
}) => (
  <Card
    padding="lg"
    radius="md"
    withBorder
    sx={{
      borderLeft: `4px solid ${BORDER_COLORS[color] ?? "#3b82f6"}`,
      background: color === "red" ? "#fff5f5" : undefined,
    }}
  >
    <Group position="apart" mb="sm">
      <ThemeIcon color={color} variant="light" size="lg" radius="md">
        {icon}
      </ThemeIcon>
      {badge && (
        <Badge variant="light" color={color} size="sm" radius="sm">
          {badge}
        </Badge>
      )}
    </Group>
    <Text
      size="xs"
      color="dimmed"
      transform="uppercase"
      weight={700}
      mb={6}
      sx={{ letterSpacing: 0.5 }}
    >
      {title}
    </Text>
    <Text
      size="2rem"
      weight={800}
      color={color === "red" ? "red" : color === "green" ? "green" : undefined}
      lh={1.1}
      mb={4}
    >
      {value}
    </Text>
    {subtitle && (
      <Text size="sm" color="dimmed" mt={2}>
        {subtitle}
      </Text>
    )}
    {alert && (
      <Text size="xs" color="red" weight={600} mt={4}>
        ⚠ {alert}
      </Text>
    )}
  </Card>
);

const ParentEngagementChart = ({
  parentsData,
  isLoading,
  dateRange,
  provenanceFilter = "all",
}) => {
  // Calcul des métriques d'engagement
  const engagementMetrics = useMemo(() => {
    if (!parentsData?.data || !dateRange) {
      return {
        totalParents: 0,
        active7Days: 0,
        active30Days: 0,
        withActivity: 0,
        neverActive: 0,
        engagementRate7: 0,
        engagementRate30: 0,
        activityRate: 0,
      };
    }
    // Utiliser la fin de la période sélectionnée comme référence
    const { start, end } = dateRange;
    const now = dayjs();
    // éviter d'utiliser une date de fin future (ex: endOf('month') dans le futur)
    const referenceEnd = end.isAfter(now) ? now : end;
    const sevenDaysAgo = referenceEnd.clone().subtract(7, "days");
    const thirtyDaysAgo = referenceEnd.clone().subtract(30, "days");

    // Filtrer TOUS les parents par provenance uniquement (on garde les parents même s'ils se sont inscrits avant la période)
    const filteredParents = parentsData.data.filter((parent) => {
      // Filtrer par provenance si un filtre est appliqué
      const matchesProvenance =
        provenanceFilter === "all" ||
        (parent.provenance &&
          parent.provenance.toLowerCase() === provenanceFilter.toLowerCase());

      return matchesProvenance;
    });

    const totalParents = filteredParents.length;

    // Parents actifs dans les 7 derniers jours (utilisation de crédit OU connexion)
    // Actifs dans les 7 jours précédant la fin de la période (inclus)
    const active7Days = filteredParents.filter((parent) => {
      const lastCreditUsage = parent.last_credit_usage
        ? dayjs(parent.last_credit_usage)
        : null;
      const lastLogin = parent.last_login ? dayjs(parent.last_login) : null;

      const creditActive =
        lastCreditUsage &&
        lastCreditUsage.isBetween(sevenDaysAgo, referenceEnd, "day", "[]");
      const loginActive =
        lastLogin &&
        lastLogin.isBetween(sevenDaysAgo, referenceEnd, "day", "[]");

      return creditActive || loginActive;
    }).length;

    // Parents actifs dans les 30 derniers jours (utilisation de crédit OU connexion)
    // Actifs dans les 30 jours précédant la fin de la période (inclus)
    const active30Days = filteredParents.filter((parent) => {
      const lastCreditUsage = parent.last_credit_usage
        ? dayjs(parent.last_credit_usage)
        : null;
      const lastLogin = parent.last_login ? dayjs(parent.last_login) : null;

      const creditActive =
        lastCreditUsage &&
        lastCreditUsage.isBetween(thirtyDaysAgo, referenceEnd, "day", "[]");
      const loginActive =
        lastLogin &&
        lastLogin.isBetween(thirtyDaysAgo, referenceEnd, "day", "[]");

      return creditActive || loginActive;
    }).length;

    // Parents avec au moins une activité (crédit ou connexion à n'importe quel moment)
    // Parents avec au moins une activité enregistrée jusqu'à la fin de la période
    const withActivity = filteredParents.filter((parent) => {
      const lastCreditUsage = parent.last_credit_usage
        ? dayjs(parent.last_credit_usage)
        : null;
      const lastLogin = parent.last_login ? dayjs(parent.last_login) : null;

      // considérer l'activité si elle a eu lieu à une date <= 'referenceEnd'
      const activityBeforeEnd =
        (lastCreditUsage &&
          (lastCreditUsage.isBefore(referenceEnd, "day") ||
            lastCreditUsage.isSame(referenceEnd, "day"))) ||
        (lastLogin &&
          (lastLogin.isBefore(referenceEnd, "day") ||
            lastLogin.isSame(referenceEnd, "day")));

      return Boolean(activityBeforeEnd);
    }).length;

    // Parents qui ne se sont jamais connectés ou n'ont jamais utilisé de crédit
    const neverActive = totalParents - withActivity;

    // Taux d'engagement
    const engagementRate7 =
      totalParents > 0 ? ((active7Days / totalParents) * 100).toFixed(1) : 0;
    const engagementRate30 =
      totalParents > 0 ? ((active30Days / totalParents) * 100).toFixed(1) : 0;
    const activityRate =
      totalParents > 0 ? ((withActivity / totalParents) * 100).toFixed(1) : 0;

    return {
      totalParents,
      active7Days,
      active30Days,
      withActivity,
      neverActive,
      engagementRate7: parseFloat(engagementRate7),
      engagementRate30: parseFloat(engagementRate30),
      activityRate: parseFloat(activityRate),
    };
  }, [parentsData, provenanceFilter, dateRange]);

  // Formater la période pour l'affichage
  const getPeriodLabel = () => {
    if (!dateRange) return "";
    const { start, end } = dateRange;

    // Si c'est aujourd'hui, afficher "Aujourd'hui"
    const now = dayjs();
    if (start.isSame(now, "day") && end.isSame(now, "day")) {
      return "Aujourd'hui";
    }

    // Si c'est la semaine en cours
    if (
      start.isSame(now.startOf("week"), "day") &&
      end.isSame(now.endOf("week"), "day")
    ) {
      return "Cette semaine";
    }

    // Si c'est le mois en cours
    if (
      start.isSame(now.startOf("month"), "day") &&
      end.isSame(now.endOf("month"), "day")
    ) {
      return "Ce mois";
    }

    // Si c'est l'année en cours
    if (
      start.isSame(now.startOf("year"), "day") &&
      end.isSame(now.endOf("year"), "day")
    ) {
      return "Cette année";
    }

    // Période personnalisée
    return `${start.format("DD MMM YYYY")} - ${end.format("DD MMM YYYY")}`;
  };

  if (isLoading) {
    return (
      <Card shadow="sm" padding="lg" radius="md" withBorder h="100%">
        <Box
          h={400}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Loader size="lg" />
        </Box>
      </Card>
    );
  }

  const neverActivePct =
    engagementMetrics.totalParents > 0
      ? parseFloat((100 - engagementMetrics.activityRate).toFixed(1))
      : 0;

  return (
    <Stack spacing="md">
      {/* Alerte si taux de dormance élevé */}
      {neverActivePct > 70 && (
        <Alert
          icon={<AlertCircle size={16} />}
          color="red"
          variant="light"
          radius="md"
          title="Taux de dormance élevé"
        >
          Plus de {neverActivePct}% des parents inscrits n'ont jamais utilisé la
          plateforme. Envisagez une campagne de réengagement.
        </Alert>
      )}

      {/* Métriques clés */}
      <SimpleGrid
        cols={4}
        spacing="md"
        breakpoints={[
          { maxWidth: "md", cols: 2 },
          { maxWidth: "sm", cols: 1 },
        ]}
      >
        <EngagementMetricCard
          title="Total Parents"
          value={engagementMetrics.totalParents.toLocaleString("fr-FR")}
          subtitle="Parents inscrits sur la plateforme"
          icon={<Users size={20} />}
          color="blue"
          badge="Total"
        />
        <EngagementMetricCard
          title="Actifs 7 jours"
          value={`${engagementMetrics.engagementRate7}%`}
          subtitle={`${engagementMetrics.active7Days} parents actifs`}
          icon={<Activity size={20} />}
          color={engagementMetrics.engagementRate7 > 5 ? "green" : "orange"}
          badge="7 jours"
        />
        <EngagementMetricCard
          title="Actifs 30 jours"
          value={`${engagementMetrics.engagementRate30}%`}
          subtitle={`${engagementMetrics.active30Days} parents actifs`}
          icon={<TrendingUp size={20} />}
          color={engagementMetrics.engagementRate30 > 10 ? "green" : "orange"}
          badge="30 jours"
        />
        <EngagementMetricCard
          title="Jamais actifs"
          value={`${neverActivePct}%`}
          subtitle={`${engagementMetrics.neverActive} parents à réengager`}
          icon={<TrendingDown size={20} />}
          color="red"
          badge="À réengager"
          alert={
            neverActivePct > 50 ? "Plus de la moitié des inscrits" : undefined
          }
        />
      </SimpleGrid>

      {/* Barre de progression globale tout en un */}
      <Card padding="lg" radius="md" withBorder>
        <Group position="apart" mb="lg">
          <Box>
            <Text size="lg" weight={700} mb={4}>
              Vue d'ensemble de l'engagement
            </Text>
            <Text size="sm" color="dimmed">
              Répartition de l'activité des parents inscrits
            </Text>
          </Box>
          <Badge variant="filled" color="blue" size="lg" radius="sm">
            {engagementMetrics.totalParents.toLocaleString("fr-FR")} PARENTS
          </Badge>
        </Group>

        {/* Barre de progression unique avec 3 sections — hauteur augmentée */}
        <Box mb="lg">
          <Progress
            size={28}
            radius="md"
            sections={[
              {
                value: engagementMetrics.engagementRate7,
                color: "green",
                label:
                  engagementMetrics.engagementRate7 > 4
                    ? `${engagementMetrics.engagementRate7}%`
                    : "",
                tooltip: `Actifs 7 derniers jours : ${engagementMetrics.active7Days} parents`,
              },
              {
                value: Math.max(
                  0,
                  engagementMetrics.engagementRate30 -
                    engagementMetrics.engagementRate7,
                ),
                color: "blue",
                label:
                  engagementMetrics.engagementRate30 -
                    engagementMetrics.engagementRate7 >
                  4
                    ? `${(engagementMetrics.engagementRate30 - engagementMetrics.engagementRate7).toFixed(1)}%`
                    : "",
                tooltip: `Actifs 8-30 jours : ${engagementMetrics.active30Days - engagementMetrics.active7Days} parents`,
              },
              {
                value: Math.max(0, 100 - engagementMetrics.engagementRate30),
                color: "red",
                label:
                  100 - engagementMetrics.engagementRate30 > 4
                    ? `${(100 - engagementMetrics.engagementRate30).toFixed(1)}%`
                    : "",
                tooltip: `Jamais actifs : ${engagementMetrics.neverActive} parents`,
              },
            ]}
          />
        </Box>

        {/* Légende claire avec chiffres mis en avant */}
        <SimpleGrid
          cols={3}
          spacing="md"
          breakpoints={[{ maxWidth: "sm", cols: 1 }]}
        >
          <Box
            p="sm"
            sx={{
              borderRadius: 8,
              background: "#f0fdf4",
              borderLeft: "3px solid #10b981",
            }}
          >
            <Group spacing={6} mb={4}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: "#10b981",
                }}
              />
              <Text size="xs" weight={700} color="dimmed" transform="uppercase">
                Actifs 7 jours
              </Text>
            </Group>
            <Text size="xl" weight={800} color="green">
              {engagementMetrics.engagementRate7}%
            </Text>
            <Text size="xs" color="dimmed" mt={2}>
              {engagementMetrics.active7Days} / {engagementMetrics.totalParents}{" "}
              parents
            </Text>
          </Box>

          <Box
            p="sm"
            sx={{
              borderRadius: 8,
              background: "#eff6ff",
              borderLeft: "3px solid #3b82f6",
            }}
          >
            <Group spacing={6} mb={4}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: "#3b82f6",
                }}
              />
              <Text size="xs" weight={700} color="dimmed" transform="uppercase">
                Actifs 30 jours
              </Text>
            </Group>
            <Text size="xl" weight={800} color="blue">
              {engagementMetrics.engagementRate30}%
            </Text>
            <Text size="xs" color="dimmed" mt={2}>
              {engagementMetrics.active30Days} /{" "}
              {engagementMetrics.totalParents} parents
            </Text>
          </Box>

          <Box
            p="sm"
            sx={{
              borderRadius: 8,
              background: "#fff5f5",
              borderLeft: "3px solid #ef4444",
            }}
          >
            <Group spacing={6} mb={4}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: 2,
                  background: "#ef4444",
                }}
              />
              <Text size="xs" weight={700} color="dimmed" transform="uppercase">
                Jamais actifs
              </Text>
            </Group>
            <Text size="xl" weight={800} color="red">
              {neverActivePct}%
            </Text>
            <Text size="xs" color="dimmed" mt={2}>
              {engagementMetrics.neverActive} / {engagementMetrics.totalParents}{" "}
              parents
            </Text>
          </Box>
        </SimpleGrid>
      </Card>
    </Stack>
  );
};

export default ParentEngagementChart;
